import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import { US_STATES, CDL_CLASSES, TRAILER_TYPES, ENDORSEMENTS, SERVICE_RADIUS_OPTIONS, slugify } from "@/components/auth/signupConstants";
import { INITIAL_DRIVER_SIGNUP_FORM, useDriverSignup } from "@/hooks/useDriverSignup";

export const DriverSignupModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(INITIAL_DRIVER_SIGNUP_FORM);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleArrayValue = (key, value) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value] }));

  const { submitting, handleSubmit } = useDriverSignup(() => toast.message("RelayRigs is waking up — retrying automatically..."));

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-signup-modal" className="max-w-md rounded-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="driver-signup-title" className="font-heading text-2xl text-rr-navy">Join the Driver Directory</DialogTitle>
          <DialogDescription>Create your RelayRigs account and get listed so carriers can find you on demand.</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} data-testid="driver-signup-form" className="space-y-3">
          <input required data-testid="driver-signup-name-input" placeholder="Full name" value={form.name} onChange={update("name")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
          <input required type="email" data-testid="driver-signup-email-input" placeholder="Email" value={form.email} onChange={update("email")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
          <input required type="password" data-testid="driver-signup-password-input" placeholder="Password" value={form.password} onChange={update("password")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />

          <div className="grid grid-cols-2 gap-3">
            <input required data-testid="driver-signup-city-input" placeholder="City" value={form.city} onChange={update("city")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
            <select required data-testid="driver-signup-state-select" value={form.state} onChange={update("state")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
              <option value="">State</option>
              {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="tel" data-testid="driver-signup-phone-input" placeholder="Phone number" value={form.phone} onChange={update("phone")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
            <select data-testid="driver-signup-service-radius-select" value={form.serviceRadius} onChange={update("serviceRadius")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
              {SERVICE_RADIUS_OPTIONS.map((r) => (<option key={r} value={r}>Up to {r} mi</option>))}
            </select>
          </div>

          <div className="pt-2 border-t border-rr-line">
            <div className="text-xs font-bold tracking-wide uppercase text-rr-muted mb-2 mt-3">Driver profile</div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="driver-signup-cdl-class-select" value={form.cdlClass} onChange={update("cdlClass")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange">
                {CDL_CLASSES.map((c) => (<option key={c} value={c}>CDL Class {c}</option>))}
              </select>
              <input data-testid="driver-signup-cdl-number-input" placeholder="CDL number" value={form.cdlNumber} onChange={update("cdlNumber")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange" />
            </div>
            <input type="number" min="0" data-testid="driver-signup-experience-input" placeholder="Years experience" value={form.experienceYears} onChange={update("experienceYears")} className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm mt-3 focus:outline-none focus:border-rr-orange" />

            <div className="mt-3">
              <div className="text-xs font-semibold text-rr-muted mb-1.5">Trailer types</div>
              <div className="flex flex-wrap gap-2">
                {TRAILER_TYPES.map((t) => (
                  <button type="button" key={t} data-testid={`driver-signup-trailer-type-${slugify(t)}`} onClick={() => toggleArrayValue("trailerTypes", t)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${form.trailerTypes.includes(t) ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted hover:border-rr-navy"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <div className="text-xs font-semibold text-rr-muted mb-1.5">Endorsements</div>
              <div className="flex flex-wrap gap-2">
                {ENDORSEMENTS.map((en) => (
                  <button type="button" key={en} data-testid={`driver-signup-endorsement-${slugify(en)}`} onClick={() => toggleArrayValue("endorsements", en)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${form.endorsements.includes(en) ? "bg-rr-orange text-white border-rr-orange" : "border-rr-line text-rr-muted hover:border-rr-orange"}`}>
                    {en}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2.5 text-xs text-rr-muted cursor-pointer pt-1">
            <input type="checkbox" data-testid="driver-signup-listed-checkbox" checked={form.listed} onChange={(e) => setForm((f) => ({ ...f, listed: e.target.checked }))} className="mt-0.5 accent-rr-orange" />
            List my profile in the public RelayRigs driver directory so carriers can find me.
          </label>

          <label className="flex items-start gap-2.5 text-xs text-rr-muted cursor-pointer pt-1">
            <input required type="checkbox" data-testid="driver-signup-age-checkbox" checked={form.ageConfirmed} onChange={(e) => setForm((f) => ({ ...f, ageConfirmed: e.target.checked }))} className="mt-0.5 accent-rr-orange" />
            I confirm that I am at least 18 years old.
          </label>

          <button type="submit" disabled={submitting || !form.ageConfirmed} data-testid="driver-signup-submit-button" className="w-full bg-rr-orange text-white font-bold py-3.5 rounded-full hover:bg-rr-navy transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Create Account & Join <ArrowRight size={16} /></>}
          </button>
          <p className="text-xs text-rr-muted text-center">
            By creating an account you agree to our{" "}
            <a href="/legal/terms-of-service" target="_blank" rel="noreferrer" className="underline hover:text-rr-orange">Terms</a> and{" "}
            <a href="/legal/privacy-policy" target="_blank" rel="noreferrer" className="underline hover:text-rr-orange">Privacy Policy</a>. You'll be redirected to a secure Stripe checkout to activate your plan.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
